import { useMemo, useState } from "react";
import useGridRowSelection from "hooks/useGridRowSelection";
import { InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGridPremium,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { plGridLocales } from "utils/muiGridLocales";
import acceptanceService, { IAcceptanceSignItem } from "api/acceptanceService";
import styles from "./styles.module.scss";

/**
 * Lista „Wysłane" w korzeniu Akceptacja i Podpis — obiekty wysłane przez
 * użytkownika do akceptacji/podpisu. Dane z BE (AcceptanceController).
 */

interface SentRow {
  id: string;
  Signature: string;
  Title: string;
  Type: string;
  Recipient: string;
  Status: string;
  SentDate: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  engagement: "Zaangażowanie",
  expense: "Wydatek",
  contract: "Umowa",
  invoice: "Faktura / Rachunek",
  case: "Sprawa / Teczka",
  EditorDocument: "Pismo",
};

/** Sygnatura z BE (pismo → sygnatura teczki); GUID nie jest sygnaturą, więc go nie pokazujemy. */
const rowSignature = (i: IAcceptanceSignItem) =>
  i.Signature ?? (i.DocumentType === "EditorDocument" ? "" : i.DocumentId);

const STATUS_LABELS: Record<string, string> = {
  Pending: "Oczekuje",
  Accepted: "Zaakceptowane",
  Signed: "Podpisane",
  Rejected: "Odrzucone",
};

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const SentList = () => {
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  const [search, setSearch] = useState("");
  const { data: items = [] } = acceptanceService.useGetSent();

  const columns: GridColDef[] = [
    { field: "Signature", headerName: "Sygnatura", width: 150 },
    { field: "Title", headerName: "Tytuł", flex: 1, minWidth: 240 },
    { field: "Type", headerName: "Typ", width: 160 },
    { field: "Recipient", headerName: "Odbiorca", width: 200 },
    { field: "Status", headerName: "Status", width: 160 },
    { field: "SentDate", headerName: "Wysłano", width: 140 },
  ];

  const rows: SentRow[] = useMemo(
    () =>
      items.map((i) => ({
        id: i.Id,
        Signature: rowSignature(i),
        Title: i.Title ?? "",
        Type: DOC_TYPE_LABELS[i.DocumentType] ?? i.DocumentType,
        Recipient: i.Sender ?? "",
        Status: STATUS_LABELS[i.Status] ?? i.Status,
        SentDate: fmtDate(i.CreatedAt),
      })),
    [items],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.Signature, r.Title, r.Type, r.Recipient, r.Status].some((f) =>
        (f ?? "").toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);
  rowsRef.current = filteredRows;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Wysłane do akceptacji/podpisu</span>
        <TextField
          size="small"
          placeholder="Szukaj na liście"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchBox}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={styles.grid}>
        <DataGridPremium
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
          }}
          localeText={plGridLocales}
          columnHeaderHeight={36}
          columns={columns}
          rows={filteredRows}
          hideFooter
          disableAggregation
          disableColumnFilter
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
};

export default SentList;
